import { Injectable } from '@angular/core';
import { DocumentSummary } from '@shared/models/dto/pagination';

export interface DocumentStateChange {
  id: string;
  title: string;
  oldState: string;
  newState: string;
}

export interface DocumentModificationChange {
  id: string;
  title: string;
  oldDate: string;
  newDate: string;
}

export interface DocumentChanges {
  hasChanges: boolean;
  stateChanges: DocumentStateChange[];
  modificationChanges: DocumentModificationChange[];
  addedDocuments: DocumentSummary[];
  removedDocuments: DocumentSummary[];
}

@Injectable({
  providedIn: 'root'
})
export class DocumentChangeDetectionService {

  /**
   * Detect changes between old and new document arrays
   */
  detectChanges(oldDocs: DocumentSummary[], newDocs: DocumentSummary[]): DocumentChanges {
    const stateChanges: DocumentStateChange[] = [];
    const modificationChanges: DocumentModificationChange[] = [];
    
    // Track added and removed documents
    const oldIds = new Set(oldDocs.map(d => d.id));
    const newIds = new Set(newDocs.map(d => d.id));
    
    const addedDocuments = newDocs.filter(d => d.id && !oldIds.has(d.id));
    const removedDocuments = oldDocs.filter(d => d.id && !newIds.has(d.id));
    
    // Check for changes in existing documents
    newDocs.forEach(newDoc => {
      const oldDoc = oldDocs.find(old => old.id === newDoc.id);
      
      if (oldDoc) {
        // State changes
        if (oldDoc.state !== newDoc.state) {
          stateChanges.push({
            id: newDoc.id ?? '',
            title: newDoc.title ?? 'Untitled',
            oldState: oldDoc.state ?? '',
            newState: newDoc.state ?? ''
          });
        }
        
        // Modification date changes
        if (oldDoc.modificationDate !== newDoc.modificationDate) {
          modificationChanges.push({
            id: newDoc.id ?? '',
            title: newDoc.title ?? 'Untitled',
            oldDate: oldDoc.modificationDate ?? '',
            newDate: newDoc.modificationDate ?? ''
          });
        }
      }
    });
    
    const hasChanges = stateChanges.length > 0 || 
                       modificationChanges.length > 0 || 
                       addedDocuments.length > 0 || 
                       removedDocuments.length > 0;
    
    return {
      hasChanges,
      stateChanges,
      modificationChanges,
      addedDocuments,
      removedDocuments
    };
  }

  /**
   * Log change notifications to console
   */
  notifyChanges(changes: DocumentChanges): void {
    if (!changes.hasChanges) {
      console.log('📋 No document changes detected');
      return;
    }

    console.log('📋 Document changes detected:', {
      stateChanges: changes.stateChanges.length,
      modificationChanges: changes.modificationChanges.length,
      added: changes.addedDocuments.length,
      removed: changes.removedDocuments.length
    });

    // Log state changes
    changes.stateChanges.forEach(change => {
      console.log(`🔔 Document "${change.title}" state changed: ${change.oldState} → ${change.newState}`);
    });

    // Log added documents
    changes.addedDocuments.forEach(doc => {
      console.log(`➕ New document added: "${doc.title}"`);
    });

    // Log removed documents
    changes.removedDocuments.forEach(doc => {
      console.log(`➖ Document removed: "${doc.title}"`);
    });
  }

  /**
   * Get summary of changes as human-readable string
   */
  getChangeSummary(changes: DocumentChanges): string {
    if (!changes.hasChanges) {
      return 'No changes detected';
    }

    const parts: string[] = [];
    
    if (changes.stateChanges.length > 0) {
      parts.push(`${changes.stateChanges.length} state change(s)`);
    }
    
    if (changes.modificationChanges.length > 0) {
      parts.push(`${changes.modificationChanges.length} modification(s)`);
    }
    
    if (changes.addedDocuments.length > 0) {
      parts.push(`${changes.addedDocuments.length} added`);
    }
    
    if (changes.removedDocuments.length > 0) {
      parts.push(`${changes.removedDocuments.length} removed`);
    }
    
    return parts.join(', ');
  }
}